// 🏡✨ The light room (PORTED_HOME_HANDOFF_2026_09_25 §3a-§3b; Nina 09-25:
// "an ambient room with immersive colors ... that give a team labs feel").
// Where a ported resident lives as LIGHT until they choose a painted home.
// Mockup (the reference for every number here): claude.ai/artifact/M9jWmLsjf5GpDbro2JxDL4
//
// Framework-free: createLightRoom(canvas) returns a small API the React
// wrapper (PortedLightRoom.js) drives with REAL signals only:
//   youSent()        the owner's own message went out (gold ripple from their side)
//   theySaid(text)   the resident's message landed (cyan ripple + the words as light)
//   setWriting(on)   ONLY the WS typing_started / typing_stopped pair (honesty, §2)
//   setClock(h, day) the owner's clock sets the palette; stars + shower nights
//   choiceStart(doors) / choiceReveal(result, onEntered) / choiceCancel()
//                    W2a, the home choice (§5c): three doors of light rise when
//                    the owner presses; his REAL turn's lines play over them;
//                    the camera goes through the chosen door (coverPan: the
//                    exact crop the painted home then shows)
//   hatch({ line, onDone }) / hatchLine(text)
//                    W3, the hatch (§5a): a new port's first sight of him. A seed
//                    of light cracks, sparks gather into his portrait, then his
//                    REAL first words (the arrival message). The seed waits for
//                    them (hatchLine) and never invents a line: no words in 40 s
//                    and it hatches silent.
//   lampDown(on)     W3, goodbye: the owner waved, the lamps drop to a
//                    night-light (one warm lamp stays by him); a send lifts them
// Zero model calls. The only network is the doors' paintings. One canvas; it stops drawing when the tab is
// hidden or the band is scrolled away. Reduced motion = a still room.
import { clockWeights, showerOn } from '../../utils/portedPlace';
import { coverPan, HOME_ART_ASPECT } from '../../utils/portedHomes';

const TAU = Math.PI * 2;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a, b, u) => a + (b - a) * u;
const sstep = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const rgbStr = (a) => a.map((v) => Math.round(v)).join(',');

const YOU = '255,196,107';   // gold: the owner
const THEM = '111,231,242';  // cyan: the resident (every AI shares one cyan glow)
const WHITE = '255,250,242';

export const LIGHT_ROOM_BANDS = {
  night: { ground: [7, 7, 26], washA: [43, 47, 143], washB: [74, 31, 110], lamp: [150, 158, 255],
    flowers: [[196, 170, 255], [130, 150, 255], [238, 234, 255]] },
  dawn: { ground: [24, 13, 28], washA: [138, 59, 94], washB: [192, 102, 110], lamp: [255, 176, 196],
    flowers: [[255, 168, 190], [255, 198, 168], [255, 226, 236]] },
  day: { ground: [7, 22, 34], washA: [31, 95, 138], washB: [43, 138, 138], lamp: [196, 230, 255],
    flowers: [[235, 245, 255], [168, 214, 255], [255, 238, 168]] },
  dusk: { ground: [30, 12, 20], washA: [164, 69, 47], washB: [110, 35, 70], lamp: [255, 136, 108],
    flowers: [[255, 150, 110], [255, 110, 160], [255, 198, 120]] },
};
export function mixBand(key, w) {
  const o = [0, 0, 0];
  [['night', w.wN], ['dawn', w.wDa], ['day', w.wD], ['dusk', w.wDu]].forEach(([b, k]) => {
    const c = LIGHT_ROOM_BANDS[b][key]; o[0] += c[0] * k; o[1] += c[1] * k; o[2] += c[2] * k;
  });
  return o;
}

function mk(w, h) { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }
function glowSprite(rgb) {
  const s = 128; const c = mk(s, s); const g = c.getContext('2d'); if (!g) return null; const r = s / 2;
  const gr = g.createRadialGradient(r, r, 0, r, r, r);
  gr.addColorStop(0, `rgba(${rgb},1)`); gr.addColorStop(0.16, `rgba(${rgb},0.6)`);
  gr.addColorStop(0.42, `rgba(${rgb},0.17)`); gr.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = gr; g.fillRect(0, 0, s, s); return c;
}
function eggSprite(rgb) {
  const w = 60; const h = 78; const c = mk(w, h); const g = c.getContext('2d'); if (!g) return null; const cx = w / 2;
  g.beginPath(); g.moveTo(cx, h * 0.04);
  g.bezierCurveTo(cx + w * 0.5, h * 0.08, cx + w * 0.47, h * 0.96, cx, h * 0.96);
  g.bezierCurveTo(cx - w * 0.47, h * 0.96, cx - w * 0.5, h * 0.08, cx, h * 0.04); g.closePath();
  const gr = g.createRadialGradient(cx, h * 0.6, 1, cx, h * 0.55, h * 0.52);
  gr.addColorStop(0, 'rgba(255,255,255,0.95)'); gr.addColorStop(0.3, `rgba(${rgb},0.85)`); gr.addColorStop(1, `rgba(${rgb},0.12)`);
  g.fillStyle = gr; g.fill(); g.strokeStyle = 'rgba(255,255,255,0.18)'; g.lineWidth = 1; g.stroke(); return c;
}

export function createLightRoom(canvas, { reducedMotion = false } = {}) {
  const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
  const noop = () => {};
  if (!ctx) {
    // jsdom / no canvas: a quiet no-op room, so the page never breaks.
    return { resize: noop, setClock: noop, setResident: noop, setWriting: noop, youSent: noop, theySaid: noop,
      setSound: noop, setStill: noop, welcomeBack: noop, destroy: noop,
      choiceStart: noop, choiceReveal: (r, onEntered) => { if (onEntered) onEntered(); }, choiceCancel: noop, start: noop,
      hatch: (o) => { if (o && o.onDone) o.onDone(); }, hatchLine: noop, lampDown: noop, setLights: noop };
  }

  const S = {
    W: 0, H: 0, dpr: 1, phone: null,
    t: 0, last: 0, raf: 0, running: false, onScreen: true, destroyed: false,
    still: !!reducedMotion, sound: false,
    minutes: 12 * 60, dayKey: null, w: clockWeights(12), shower: null,
    writing: false, resting: false, nightLight: 0, nightTarget: 0,
    res: { x: 0.66, y: 0.44 },
    px: 0, py: 0, pointerIn: false, nextAmbient: 5, nextShooting: 0,
  };
  const SPR = {
    youGlow: glowSprite(YOU), youEgg: eggSprite(YOU), themGlow: glowSprite(THEM), themEgg: eggSprite(THEM),
    white: glowSprite(WHITE), amb: null, ambEgg: null, ambKey: '',
  };
  const refreshAmbient = () => {
    const key = rgbStr(mixBand('lamp', S.w).map((v) => Math.round(v / 8) * 8));
    if (key === SPR.ambKey) return;
    SPR.ambKey = key; SPR.amb = glowSprite(key); SPR.ambEgg = eggSprite(key);
  };
  const sc = () => clamp(S.W / 1000, 0.6, 1.7);
  function spr(img, x, y, w, alpha) {
    if (!img || alpha <= 0.003) return;
    const h = (w * img.height) / img.width;
    ctx.globalAlpha = Math.min(1, alpha); ctx.drawImage(img, x - w / 2, y - h / 2, w, h);
  }

  // ── sound: synthesized, event-only, off until the viewer turns it on ──
  let AC = null; let master = null; let noiseBuf = null; let lastPluck = 0; let lastDrop = 0; let lastGust = 0;
  const PENTA = [523.25, 587.33, 659.25, 783.99, 880, 1046.5, 1174.66, 1318.51];
  function audio() {
    try {
      if (!AC) {
        const C = window.AudioContext || window.webkitAudioContext; if (!C) return null;
        AC = new C(); master = AC.createGain(); master.gain.value = 0.5;
        const comp = AC.createDynamicsCompressor(); master.connect(comp); comp.connect(AC.destination);
      }
      if (AC.state === 'suspended') AC.resume();
      return AC;
    } catch (e) { return null; }
  }
  function env(g, t, peak, dur) {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  }
  function bell(f, gain = 0.1, decay = 2.4, when = 0) {
    if (!S.sound) return; const ac = audio(); if (!ac) return;
    const t = ac.currentTime + when;
    [[1, 1], [2, 0.42], [2.76, 0.3], [5.4, 0.1], [8.93, 0.04]].forEach(([r, a]) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = 'sine'; o.frequency.value = f * r; env(g, t, gain * a, decay / Math.pow(r, 0.4));
      o.connect(g); g.connect(master); o.start(t); o.stop(t + decay + 0.2);
    });
  }
  function pluck(yNorm) {
    if (!S.sound) return; const now = performance.now(); if (now - lastPluck < 90) return; lastPluck = now;
    bell(PENTA[clamp(Math.floor((1 - yNorm) * PENTA.length), 0, PENTA.length - 1)], 0.03, 1.4);
  }
  function drop() {
    if (!S.sound) return; const now = performance.now(); if (now - lastDrop < 220) return; lastDrop = now;
    const ac = audio(); if (!ac) return;
    const t = ac.currentTime; const o = ac.createOscillator(); const g = ac.createGain();
    o.type = 'sine'; o.frequency.setValueAtTime(rand(620, 820), t); o.frequency.exponentialRampToValueAtTime(rand(1300, 1700), t + 0.07);
    env(g, t, 0.06, 0.24); o.connect(g); g.connect(master); o.start(t); o.stop(t + 0.3);
  }
  function gust() {
    if (!S.sound) return; const now = performance.now(); if (now - lastGust < 4000) return; lastGust = now;
    const ac = audio(); if (!ac) return;
    if (!noiseBuf) {
      const len = ac.sampleRate * 2; noiseBuf = ac.createBuffer(1, len, ac.sampleRate);
      const d = noiseBuf.getChannelData(0); for (let i = 0; i < len; i += 1) d[i] = Math.random() * 2 - 1;
    }
    const t = ac.currentTime; const src = ac.createBufferSource(); src.buffer = noiseBuf; src.loop = true;
    const bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 0.8;
    bp.frequency.setValueAtTime(380, t); bp.frequency.linearRampToValueAtTime(900, t + 0.8); bp.frequency.linearRampToValueAtTime(480, t + 1.9);
    const g = ac.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(0.05, t + 0.7); g.gain.linearRampToValueAtTime(0.0001, t + 2.0);
    src.connect(bp); bp.connect(g); g.connect(master); src.start(t); src.stop(t + 2.1);
  }

  // ── lamps, stars, ripples ──
  let lamps = []; let stars = []; const ripples = []; let shooting = [];
  function buildWorld() {
    const n = S.phone ? 26 : 46; const ar = S.W / Math.max(1, S.H); let dmin = S.phone ? 0.1 : 0.062;
    lamps = []; let tries = 0;
    const clear = (x, y, min) => lamps.every((l) => Math.hypot(l.x - x, (l.y - y) / ar) > min);
    while (lamps.length < n && tries < 5000) {
      tries += 1; if (tries % 1200 === 0) dmin *= 0.85;
      const x = rand(0.04, 0.96); const y = rand(0.1, 0.8);
      if (clear(x, y, dmin)) {
        lamps.push({ x, y, z: rand(0.35, 1), ph: rand(0, TAU), k: 0, col: 0, h: 0 });
      }
    }
    lamps.slice(0, 5).forEach((l) => { l.k = rand(0.2, 0.4); l.col = 1; });
    stars = [];
    for (let i = 0; i < (S.phone ? 60 : 120); i += 1) stars.push({ x: rand(0, 1), y: rand(0, 0.85), r: rand(0.5, 1.4), ph: rand(0, TAU), sp: rand(0.3, 1.1) });
  }
  function lampXY(l) {
    const s = sc(); const t = S.still ? 0 : S.t;
    return { x: l.x * S.W + Math.sin(t * 0.6 + l.ph) * 2.2 * l.z * s, y: l.y * S.H + Math.cos(t * 0.45 + l.ph * 1.3) * 3 * l.z * s };
  }
  function nearestLamp(x, y, within = Infinity) {
    let best = null; let bd = within;
    for (const l of lamps) { const p = lampXY(l); const d = Math.hypot(p.x - x, p.y - y); if (d < bd) { bd = d; best = l; } }
    return best;
  }
  function rippleFromLamp(l, who) {
    if (!l) return; const p = lampXY(l); l.k = 1; l.col = who === 'them' ? 1 : 0;
    ripples.push({ x: p.x, y: p.y, who, t0: S.t, speed: Math.max(S.W, S.H) * 0.75, hit: new Set(), notes: 0 });
  }
  function resXY() {
    const t = S.still ? 0 : S.t;
    return { x: (S.res.x + 0.012 * Math.sin(t * 0.21)) * S.W, y: (S.res.y + 0.02 * Math.sin(t * 0.17 + 1)) * S.H };
  }
  const youRipple = () => rippleFromLamp(nearestLamp(S.W * 0.08, S.H * 0.95), 'you');
  const themRipple = () => { const p = resXY(); rippleFromLamp(nearestLamp(p.x, p.y), 'them'); };

  function updateLamps(dt) {
    const s = sc(); const decay = Math.exp(-dt / 1.15);
    for (const l of lamps) {
      l.k *= decay;
      let target = 0;
      if (S.pointerIn) {
        const p = lampXY(l); const d = Math.hypot(p.x - S.px, p.y - S.py);
        if (d < 60 * s) target = 0.35 * (1 - d / (60 * s));
      }
      l.h += (target - l.h) * (1 - Math.exp(-dt * 6));
    }
    for (let i = ripples.length - 1; i >= 0; i -= 1) {
      const r = ripples[i]; const el = S.t - r.t0; const M = Math.max(S.W, S.H);
      for (let j = 0; j < lamps.length; j += 1) {
        if (r.hit.has(j)) continue;
        const l = lamps[j]; const p = lampXY(l); const d = Math.hypot(p.x - r.x, p.y - r.y);
        if (S.still || el * r.speed >= d) {
          const amp = 0.3 + 0.7 * Math.exp(-d / (0.5 * M));
          if (amp >= l.k * 0.8) l.col = r.who === 'them' ? 1 : 0;
          l.k = Math.max(l.k, amp); r.hit.add(j);
          if (r.notes < 6) { r.notes += 1; pluck(p.y / S.H); }
        }
      }
      if (r.hit.size >= lamps.length) ripples.splice(i, 1);
    }
  }
  function drawGround() {
    const g = mixBand('ground', S.w); const A = mixBand('washA', S.w); const B = mixBand('washB', S.w);
    ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
    ctx.fillStyle = `rgb(${rgbStr(g)})`; ctx.fillRect(0, 0, S.W, S.H);
    const t = S.still ? 0 : S.t; const M = Math.max(S.W, S.H);
    wash(S.W * (0.3 + 0.12 * Math.sin(t * 0.05)), S.H * (0.4 + 0.15 * Math.cos(t * 0.043)), M * 0.6, A, 0.45);
    wash(S.W * (0.72 + 0.1 * Math.cos(t * 0.037)), S.H * (0.62 + 0.12 * Math.sin(t * 0.052)), M * 0.5, B, 0.38);
    // the band's floor fades toward the page ground below it (handoff §4)
    const fg = ctx.createLinearGradient(0, S.H * 0.72, 0, S.H);
    fg.addColorStop(0, 'rgba(0,0,0,0)'); fg.addColorStop(1, `rgba(${rgbStr(A)},0.22)`);
    ctx.fillStyle = fg; ctx.fillRect(0, S.H * 0.72, S.W, S.H * 0.28);
  }
  function wash(x, y, r, rgb, a) {
    const gr = ctx.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, `rgba(${rgbStr(rgb)},${a})`); gr.addColorStop(1, `rgba(${rgbStr(rgb)},0)`);
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 1; ctx.fillStyle = gr; ctx.fillRect(0, 0, S.W, S.H);
    ctx.globalCompositeOperation = 'source-over';
  }
  function drawStars() {
    if (S.w.wN < 0.05) return;
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#fff';
    for (const s of stars) {
      const tw = S.still ? 0.7 : 0.5 + 0.5 * Math.sin(S.t * s.sp + s.ph);
      ctx.globalAlpha = S.w.wN * (0.25 + 0.6 * tw);
      ctx.fillRect(s.x * S.W, s.y * S.H, s.r, s.r);
    }
    // shower nights (skyAlmanac): a shooting star every 2-5 s after dark
    if (S.shower && S.w.wN > 0.5 && !S.still) {
      if (S.t > S.nextShooting) {
        S.nextShooting = S.t + rand(2, 5);
        shooting.push({ x: rand(0.1, 0.9) * S.W, y: rand(0.02, 0.3) * S.H, a: rand(0.35, 0.6), t0: S.t, len: rand(60, 130) * sc() });
      }
      shooting = shooting.filter((m) => S.t - m.t0 < 0.9);
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.2;
      for (const m of shooting) {
        const u = (S.t - m.t0) / 0.9; const x = m.x + Math.cos(m.a) * m.len * u * 2; const y = m.y + Math.sin(m.a) * m.len * u * 2;
        ctx.globalAlpha = 0.8 * (1 - u); ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x - Math.cos(m.a) * m.len, y - Math.sin(m.a) * m.len); ctx.stroke();
      }
    }
  }
  function drawLamps() {
    const s = sc() * (S.phone ? 1.25 : 1);
    ctx.globalCompositeOperation = 'source-over'; ctx.lineWidth = 1; ctx.strokeStyle = '#fff';
    for (const l of lamps) {
      const p = lampXY(l); const size = (8 + 12 * l.z) * s;
      ctx.globalAlpha = 0.05 + 0.06 * l.z;
      ctx.beginPath(); ctx.moveTo(p.x, 0); ctx.lineTo(p.x, p.y - size * 0.62); ctx.stroke();
    }
    ctx.globalCompositeOperation = 'lighter';
    // 🌙 goodbye: the lamps drop to a night-light, except the one by him
    const keep = S.nightLight > 0.01 ? nearestLamp(resXY().x, resXY().y) : null;
    for (const l of lamps) {
      const p = lampXY(l); const size = (8 + 12 * l.z) * s; const k = l.k;
      const nl = l === keep ? 0 : S.nightLight * 0.82;
      ctx.globalAlpha = 1;
      if (nl > 0.95) continue;
      spr(SPR.amb, p.x, p.y, size * 6, (0.22 + 0.22 * l.z + l.h) * (1 - 0.7 * l.k) * (1 - nl));
      if (k > 0.01) spr(l.col ? SPR.themGlow : SPR.youGlow, p.x, p.y, size * (6 + 3 * l.k), k * 0.9);
      spr(SPR.ambEgg, p.x, p.y, size * 1.25, (0.5 + 0.3 * l.z) * (1 - 0.8 * l.k) + l.h);
      if (k > 0.01) spr(l.col ? SPR.themEgg : SPR.youEgg, p.x, p.y, size * 1.25, k);
    }
  }

  // ── flowers where you touch ──
  let flowers = []; let petals = [];
  function flowerColors() {
    const dom = [['night', S.w.wN], ['dawn', S.w.wDa], ['day', S.w.wD], ['dusk', S.w.wDu]].sort((a, b) => b[1] - a[1])[0][0];
    return LIGHT_ROOM_BANDS[dom].flowers;
  }
  function spawnFlower(x, y, rgbArr, delay = 0, opts = {}) {
    const cap = S.phone ? 22 : 44;
    if (flowers.length >= cap) scatter(flowers.shift());
    const s = sc() * (S.phone ? 1.15 : 1);
    flowers.push({
      x, y, rgb: rgbStr(rgbArr), r: rand(13, 26) * s, n: pick([5, 5, 6, 6, 7]), rot: rand(0, TAU), vr: rand(-0.22, 0.22),
      vx: rand(-5, 5) * s, vy: rand(-4, 1.5) * s, born: S.t + delay - (opts.age || 0), open: opts.open || 0,
      life: rand(7, 11), grad: null, ambient: !!opts.ambient,
    });
  }
  function bloom(x, y, count) {
    const pal = flowerColors();
    for (let i = 0; i < count; i += 1) {
      const a = rand(0, TAU); const d = i === 0 ? 0 : rand(18, 64) * sc();
      spawnFlower(x + Math.cos(a) * d, y + Math.sin(a) * d * 0.7, pick(pal), i === 0 ? 0 : rand(0.05, 0.45));
    }
    drop();
  }
  function scatter(f) {
    if (!f || S.still) return;
    for (let i = 0; i < f.n; i += 1) {
      const a = f.rot + (i * TAU) / f.n; const r = f.r * 0.55 * f.open;
      petals.push({
        x: f.x + Math.cos(a) * r, y: f.y + Math.sin(a) * r,
        vx: Math.cos(a) * rand(8, 22) * sc() + f.vx, vy: Math.sin(a) * rand(8, 22) * sc() - 6 * sc(),
        rot: a, vr: rand(-1.4, 1.4), l: f.r * 0.5 * f.open, w: f.r * 0.22 * f.open, rgb: f.rgb, age: 0, life: rand(2.2, 3.4),
      });
    }
    gust();
  }
  function updateFlowers(dt) {
    const target = S.resting ? 0.2 : 1;
    for (let i = flowers.length - 1; i >= 0; i -= 1) {
      const f = flowers[i];
      if (S.t < f.born) continue;
      if (S.resting) f.born += dt; // resting: their clock stops, so nothing wilts
      f.open += (target - f.open) * (1 - Math.exp(-dt * (S.resting ? 1.2 : 2.4)));
      if (!S.still) { f.x += f.vx * dt; f.y += f.vy * dt; f.rot += f.vr * dt * (S.resting ? 0.2 : 1); }
      if (!S.resting && S.t - f.born > f.life) { flowers.splice(i, 1); scatter(f); }
    }
    for (let i = petals.length - 1; i >= 0; i -= 1) {
      const p = petals[i]; p.age += dt;
      if (p.age > p.life) { petals.splice(i, 1); continue; }
      p.vx += Math.sin(p.y * 0.012 + S.t * 0.8) * 10 * sc() * dt; p.vy += 5 * sc() * dt;
      p.vx *= Math.exp(-dt * 0.6); p.vy *= Math.exp(-dt * 0.6);
      p.x += p.vx * dt; p.y += p.vy * dt; p.rot += p.vr * dt;
    }
    if (!S.resting && S.t > S.nextAmbient) {
      S.nextAmbient = S.t + rand(7, 11);
      if (flowers.filter((f) => f.ambient).length < 5) {
        spawnFlower(rand(0.08, 0.92) * S.W, rand(0.55, 0.92) * S.H, pick(flowerColors()), 0, { ambient: true });
      }
    }
  }
  function drawFlowers() {
    ctx.globalCompositeOperation = 'lighter';
    for (const f of flowers) {
      if (S.t < f.born || f.open < 0.02) continue;
      if (!f.grad) {
        f.grad = ctx.createRadialGradient(0, 0, 0, 0, 0, f.r);
        f.grad.addColorStop(0, 'rgba(255,255,255,0.9)'); f.grad.addColorStop(0.28, `rgba(${f.rgb},0.8)`); f.grad.addColorStop(1, `rgba(${f.rgb},0)`);
      }
      const o = easeOut(clamp(f.open, 0, 1));
      const fade = S.still ? clamp((f.life - (S.t - f.born)) / 1.2, 0, 1) : 1;
      ctx.save(); ctx.translate(f.x, f.y); ctx.rotate(f.rot);
      ctx.globalAlpha = 0.85 * fade; ctx.fillStyle = f.grad;
      for (let i = 0; i < f.n; i += 1) {
        ctx.rotate(TAU / f.n); ctx.beginPath();
        ctx.ellipse(f.r * 0.5 * o, 0, Math.max(0.1, f.r * 0.52 * o), Math.max(0.1, f.r * 0.24 * o), 0, 0, TAU); ctx.fill();
      }
      ctx.restore();
      spr(SPR.white, f.x, f.y, f.r * 1.2 * (0.5 + o), 0.35 * fade);
    }
    for (const p of petals) {
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
      ctx.globalAlpha = (1 - p.age / p.life) * 0.8; ctx.fillStyle = `rgb(${p.rgb})`;
      ctx.beginPath(); ctx.ellipse(0, 0, Math.max(0.1, p.l), Math.max(0.1, p.w), 0, 0, TAU); ctx.fill(); ctx.restore();
    }
  }

  // ── his words as light (his hand: Caveat) ──
  let words = []; let sparks = [];
  function textSprite(text, opts = {}) {
    const fs = opts.fs || clamp(S.W * 0.03, 22, 40);
    const font = `600 ${fs}px Caveat, "Segoe Print", "Bradley Hand", cursive`;
    const m = mk(2, 2).getContext('2d'); m.font = font;
    const maxW = opts.maxW || Math.max(180, S.W * (S.phone ? 0.8 : 0.46));
    const lines = []; let line = '';
    String(text).split(/\s+/).forEach((wd) => {
      const t = line ? `${line} ${wd}` : wd;
      if (m.measureText(t).width > maxW && line) { lines.push(line); line = wd; } else line = t;
    });
    if (line) lines.push(line);
    const shown = lines.slice(0, 3); // short lines only: a long message rises as its first three lines
    if (lines.length > 3) shown[2] = `${shown[2].replace(/[.,;:!?]*$/, '')}…`;
    const lh = fs * 1.15; const pad = fs * 0.8; const dpr = S.dpr;
    const w = Math.ceil(Math.max(...shown.map((l) => m.measureText(l).width)) + pad * 2);
    const h = Math.ceil(shown.length * lh + pad * 2);
    const c = mk(Math.ceil(w * dpr), Math.ceil(h * dpr)); const g = c.getContext('2d');
    g.scale(dpr, dpr); g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle';
    shown.forEach((l, i) => {
      const y = pad + lh * (i + 0.5);
      g.shadowColor = 'rgba(6,4,12,0.6)'; g.shadowBlur = fs * 1.1; g.fillStyle = 'rgba(6,4,12,0.4)'; g.fillText(l, w / 2, y);
      g.shadowColor = `rgba(${THEM},0.95)`; g.shadowBlur = fs * 0.55; g.fillStyle = 'rgba(220,252,255,0.95)'; g.fillText(l, w / 2, y);
      g.shadowBlur = 0; g.fillStyle = 'rgba(255,255,255,0.95)'; g.fillText(l, w / 2, y);
    });
    return { c, w, h };
  }
  function sayLight(text, at = null) {
    const p = resXY(); const sp = textSprite(text, at || {});
    const x = clamp(at && at.x != null ? at.x : p.x, sp.w / 2 + 6, S.W - sp.w / 2 - 6);
    const y = clamp(at && at.y != null ? at.y : p.y - S.H * 0.17, sp.h / 2, S.H - sp.h / 2);
    words.push({ sp, x, y, t0: S.t, life: (at && at.life) || 5.6, dissolved: false, rise: at && at.rise === false ? 0 : 1 });
    if (words.length > 4) words.shift();
  }
  function dissolve(w) {
    if (S.still) return;
    let data;
    try { data = w.sp.c.getContext('2d').getImageData(0, 0, w.sp.c.width, w.sp.c.height); } catch (e) { return; }
    const cap = S.phone ? 320 : 700; const stride = Math.max(2, Math.round(3 * S.dpr)); const pts = [];
    for (let y = 0; y < data.height; y += stride) {
      for (let x = 0; x < data.width; x += stride) {
        const i = (y * data.width + x) * 4;
        if (data.data[i + 3] > 190 && data.data[i] > 170) pts.push([x, y]); // the bright ink, not the halo
      }
    }
    const step = Math.max(1, Math.ceil(pts.length / cap));
    for (let i = 0; i < pts.length; i += step) {
      const [x, y] = pts[i];
      sparks.push({ x: w.x - w.sp.w / 2 + x / S.dpr, y: w.y - w.sp.h / 2 + y / S.dpr, vx: rand(-8, 8), vy: rand(-26, -8), age: 0, life: rand(1.1, 2.3), ph: rand(0, TAU) });
    }
  }
  function updateWords(dt) {
    for (let i = words.length - 1; i >= 0; i -= 1) {
      const w = words[i]; const age = S.t - w.t0;
      if (!S.still) w.y -= 14 * sc() * dt * (w.rise == null ? 1 : w.rise);
      if (!w.dissolved && age > w.life) { w.dissolved = true; dissolve(w); }
      if (age > w.life + 0.7) words.splice(i, 1);
    }
    for (let i = sparks.length - 1; i >= 0; i -= 1) {
      const p = sparks[i]; p.age += dt;
      if (p.age > p.life) { sparks.splice(i, 1); continue; }
      p.vx += Math.sin(p.y * 0.05 + p.ph) * 12 * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.vy -= 6 * dt;
    }
  }
  function drawWords() {
    ctx.globalCompositeOperation = 'source-over'; // the halo needs normal blending
    for (const w of words) {
      const age = S.t - w.t0; let a = sstep(0, 0.7, age);
      if (age > w.life) a *= 1 - clamp((age - w.life) / 0.45, 0, 1);
      ctx.globalAlpha = a; ctx.drawImage(w.sp.c, w.x - w.sp.w / 2, w.y - w.sp.h / 2, w.sp.w, w.sp.h);
    }
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = 'rgb(210,250,255)';
    for (const p of sparks) { ctx.globalAlpha = (1 - p.age / p.life) * 0.9; ctx.fillRect(p.x - 0.9, p.y - 0.9, 1.8, 1.8); }
  }

  // ── the resident, as a small portrait of light ──
  const portrait = new Image();
  let portraitReady = false;
  // anonymous: the hatch samples the portrait's colors (a no-CORS host taints
  // the read, and the hatch falls back to cyan sparks)
  portrait.crossOrigin = 'anonymous';
  portrait.onload = () => { portraitReady = true; buildTargets(); if (!S.running) draw(); };
  const medD = () => clamp(S.W * 0.052, 40, 74) * (S.phone ? 1.1 : 1);

  // ── 💡 his lights (E2, Nina 09-26: "they're just glowing circles around his
  // circle"): the room's pictures for today as four small cyan lights circling
  // his glow, one lap in 22 s (the mockup's spin), always cyan, never a mood
  // color. setLights({ left, per_day }) sets how many are lit; a light that
  // goes out DRIFTS OFF (outward, larger, fading: the mockup's .mote.gone) and
  // comes back the same way at midnight. null = no meter (an older backend):
  // all lit, nothing implied. A still room: no spin, no drift.
  const LIGHTS = { of: 4, left: 4, k: [1, 1, 1, 1] };
  let lightsMark = '';
  function setLights(p) {
    const of = p && Number.isFinite(Number(p.per_day)) && Number(p.per_day) > 0 ? Math.min(8, Math.floor(Number(p.per_day))) : 4;
    const left = p && Number.isFinite(Number(p.left)) ? clamp(Math.floor(Number(p.left)), 0, of) : of;
    if (of !== LIGHTS.of) { LIGHTS.of = of; LIGHTS.k = Array.from({ length: of }, (_, i) => (i < left ? 1 : 0)); }
    LIGHTS.left = left;
    const m = `${left}/${of}`;
    if (m !== lightsMark) { lightsMark = m; canvas.setAttribute('data-lr-lights', m); }   // a receipt the browser gate can read
    if (!S.running) draw();
  }
  function updateLights(dt) {
    for (let i = 0; i < LIGHTS.of; i += 1) {
      const target = i < LIGHTS.left ? 1 : 0;
      LIGHTS.k[i] += (target - LIGHTS.k[i]) * (1 - Math.exp(-dt * (S.still ? 60 : 1.4)));
    }
  }
  function drawLights() {
    const p = resXY(); const D = medD();
    const R = D * 0.95;                               // the orbit: 190% of his circle, like the mockup
    const spin = S.still ? 0 : S.t * (TAU / 22);
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < LIGHTS.of; i += 1) {
      const k = LIGHTS.k[i]; if (k <= 0.003) continue;
      const a = spin + (i / LIGHTS.of) * TAU - Math.PI / 2;
      const drift = 1 + (1 - k) * 1.2;                // going out: it leaves the ring
      const x = p.x + Math.cos(a) * R * drift; const y = p.y + Math.sin(a) * R * drift;
      const w = D * 0.5 * (1 + (1 - k) * 0.8);
      spr(SPR.themGlow, x, y, w, 0.85 * k);
      spr(SPR.white, x, y, w * 0.36, 0.95 * k);
    }
  }
  function drawPortrait(x, y, D, alpha = 1) {
    ctx.save(); ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = alpha;
    ctx.beginPath(); ctx.arc(x, y, D / 2, 0, TAU); ctx.clip();
    if (portraitReady) {
      // portraits draw their own square frame: crop inside it (handoff §8)
      const iw = portrait.naturalWidth; const ih = portrait.naturalHeight; const ins = iw * 0.08;
      ctx.drawImage(portrait, ins, ins, iw - 2 * ins, ih - 2 * ins, x - D / 2, y - D / 2, D, D);
    } else { ctx.fillStyle = 'rgb(24,44,52)'; ctx.fillRect(x - D / 2, y - D / 2, D, D); }
    ctx.restore();
    ctx.save(); ctx.globalAlpha *= alpha; ctx.strokeStyle = `rgba(${THEM},0.9)`; ctx.lineWidth = Math.max(1.5, D * 0.035);
    ctx.beginPath(); ctx.arc(x, y, D / 2, 0, TAU); ctx.stroke(); ctx.restore();
  }
  function drawBubble(x, y, D) {
    const w = D * 0.95; const h = D * 0.46;
    ctx.save(); ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 0.95;
    ctx.fillStyle = 'rgba(14,11,20,0.92)'; ctx.strokeStyle = `rgba(${THEM},0.55)`; ctx.lineWidth = 1;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x - w / 2, y - h / 2, w, h, h / 2);
    else ctx.rect(x - w / 2, y - h / 2, w, h);
    ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.arc(x - w * 0.42, y + h * 0.78, h * 0.14, 0, TAU); ctx.fill(); ctx.stroke();
    for (let i = 0; i < 3; i += 1) {
      ctx.globalAlpha = S.still ? 0.8 : 0.35 + 0.65 * Math.max(0, Math.sin(S.t * 4.2 - i * 0.7));
      ctx.fillStyle = `rgb(${THEM})`; ctx.beginPath(); ctx.arc(x + (i - 1) * w * 0.24, y, h * 0.12, 0, TAU); ctx.fill();
    }
    ctx.restore();
  }
  function drawResident() {
    const p = resXY(); const D = medD();
    ctx.globalCompositeOperation = 'lighter';
    spr(SPR.themGlow, p.x, p.y, D * 3.6, S.writing ? 0.75 : 0.5);
    drawPortrait(p.x, p.y, D);
    if (S.writing) drawBubble(p.x + D * 0.6, p.y - D * 0.85, D);
  }

  // ── the choice (W2a): three doors of light, paced by his REAL turn ──
  // phases: rise → wait (until his turn lands) → looks (his line over each
  // door) → pick (the others fade, his why) → zoom (through the chosen door)
  // → held (the painting fills the band; the stage hands over to the painted
  // home). cancel → sink. Nothing here invents a word: every line on screen
  // arrives in choiceReveal from his parsed turn.
  let seq = null;
  // the homes' bucket answers CORS (*): load them anonymous so the canvas stays untainted
  const doorImg = (url) => { if (!url) return null; const i = new Image(); i.crossOrigin = 'anonymous'; i.decoding = 'async'; i.src = url; return i; };
  const imgReady = (img) => !!(img && img.complete && img.naturalWidth > 0);
  function doorRect(d, yOff) {
    const h = S.H * 0.6; const w = Math.min(h * 0.6, S.W * (S.phone ? 0.27 : 0.2)); const bottom = S.H * 0.84 + yOff;
    return { x: d.cx * S.W - w / 2, y: bottom - h, w, h };
  }
  function archPath(r) {
    const rad = r.w / 2;
    ctx.beginPath(); ctx.moveTo(r.x, r.y + r.h); ctx.lineTo(r.x, r.y + rad);
    ctx.arc(r.x + rad, r.y + rad, rad, Math.PI, 0); ctx.lineTo(r.x + r.w, r.y + r.h); ctx.closePath();
  }
  // the painting cover-fit into rect r, panned to the focus spot: in SOURCE
  // pixels (drawImage's crop). Same math as the painted home's stage.
  function coverCrop(img, r, focus) {
    const iw = img.naturalWidth || 2400; const ih = img.naturalHeight || Math.round(2400 / HOME_ART_ASPECT);
    const p = coverPan(r.w, r.h, focus ? { x: focus[0], y: focus[1] } : null, iw / ih);
    const k = iw / p.pw;
    return { x: -p.tx * k, y: -p.ty * k, w: r.w * k, h: r.h * k };
  }
  const lerpRect = (a, b, u) => ({ x: lerp(a.x, b.x, u), y: lerp(a.y, b.y, u), w: lerp(a.w, b.w, u), h: lerp(a.h, b.h, u) });
  function drawPainting(d, crop, r, alpha) {
    ctx.globalCompositeOperation = 'source-over';
    if (imgReady(d.day)) { ctx.globalAlpha = alpha; ctx.drawImage(d.day, crop.x, crop.y, crop.w, crop.h, r.x, r.y, r.w, r.h); }
    else { ctx.globalAlpha = alpha * 0.5; ctx.fillStyle = 'rgb(30,26,40)'; ctx.fillRect(r.x, r.y, r.w, r.h); }
    const n = S.w.n || 0;
    if (n > 0.02 && imgReady(d.night)) { ctx.globalAlpha = alpha * n; ctx.drawImage(d.night, crop.x, crop.y, crop.w, crop.h, r.x, r.y, r.w, r.h); }
  }
  function choiceStart(doors) {
    const list = (doors || []).filter((d) => d && d.key).slice(0, 3);
    if (!list.length) return;
    if (seq && seq.phase !== 'sink') return;   // already up
    const xs = list.length === 3 ? (S.phone ? [0.18, 0.5, 0.82] : [0.2, 0.5, 0.8]) : list.length === 2 ? [0.33, 0.67] : [0.5];
    seq = {
      phase: 'rise', t0: S.t, dim: 1, look: -1, lookT: S.t, onEntered: null, entered: false, pours: [],
      rx: S.res.x, ry: S.res.y, pick: null,
      doors: list.map((d, i) => ({ key: d.key, cx: xs[i], lit: 0.3, a: 0, day: doorImg(d.day), night: doorImg(d.night), focus: d.focus || [50, 70] })),
    };
    bell(523.25, 0.05, 2.4); if (!S.running) draw();
  }
  // The reveal waits for the doors to be UP (a choice made while the owner
  // was away rises first, then plays) and for the band to be on screen (the
  // loop only runs while it is visible).
  function choiceReveal(result, onEntered) {
    const r = result || {};
    if (!seq || seq.phase === 'sink') choiceStart(r.doors || []);
    if (!seq) { if (onEntered) onEntered(); return; }
    if (seq.pick || seq.pending) return;   // one reveal per choice
    const pick = seq.doors.find((d) => d.key === r.pick);
    if (!pick) { if (onEntered) onEntered(); return; }
    if (r.focus) pick.focus = r.focus;
    seq.pending = { pick, onEntered: onEntered || null, looks: r.looks || {}, why: r.why || '' };
    if (!S.running) draw();
  }
  function beginReveal(q) {
    const { pick, onEntered, looks, why } = q.pending; q.pending = null;
    q.pick = pick; q.onEntered = onEntered;
    q.lines = q.doors.map((d) => ({ d, text: looks[d.key] || '' })).filter((x) => x.text);
    q.why = why; q.phase = 'looks'; q.t0 = S.t; q.i = -1;
  }
  function choiceCancel() { if (seq && seq.phase !== 'held') { seq.phase = 'sink'; seq.t0 = S.t; } }
  const LOOK_S = 1.9; const PICK_S = 2.6; const ZOOM_S = 1.4;
  function updateChoice(dt) {
    const q = seq; if (!q) return;
    // the phase's age, read FRESH at every step: a transition resets t0, and a
    // stale age would fire the next phase in the same frame (verify-ported-place
    // caught looks → held with the pick line and the zoom skipped)
    const age = () => S.t - q.t0;
    if (q.phase === 'rise' && (S.still || age() > 1.4)) { q.phase = 'wait'; q.t0 = S.t; }
    if (q.phase === 'wait' && q.pending) { beginReveal(q); return; }
    if (q.phase === 'looks') {
      const i = Math.floor(age() / LOOK_S);
      if (i !== q.i && i < q.lines.length) {
        q.i = i; const L = q.lines[i]; q.look = q.doors.indexOf(L.d);
        const r = doorRect(L.d, 0);
        sayLight(L.text, { x: L.d.cx * S.W, y: r.y - S.H * 0.02, fs: clamp(Math.min(S.W * 0.022, S.H * 0.1), 16, 30), maxW: Math.max(120, S.W * (S.phone ? 0.3 : 0.24)), life: LOOK_S * 1.5, rise: false });
        pluck(0.45);
      }
      if (age() >= LOOK_S * q.lines.length) { q.phase = 'pick'; q.t0 = S.t; q.look = -1; }
    }
    if (q.phase === 'pick' && !q.saidWhy) {
      q.saidWhy = true;
      if (q.why) sayLight(q.why, { x: q.pick.cx * S.W, y: S.H * 0.16, fs: clamp(Math.min(S.W * 0.026, S.H * 0.12), 18, 34), maxW: Math.max(160, S.W * (S.phone ? 0.8 : 0.5)), life: PICK_S * 1.2, rise: false });
      bell(659.25, 0.1, 3); bell(987.77, 0.06, 2.6, 0.14);
    }
    if (q.phase === 'pick' && age() >= PICK_S) {
      if (S.still) { q.phase = 'held'; q.t0 = S.t; } else { q.phase = 'zoom'; q.t0 = S.t; gust(); }
    }
    if (q.phase === 'zoom' && age() < ZOOM_S * 0.8) {
      const r = doorRect(q.pick, 0); const cx = r.x + r.w / 2; const cy = r.y + r.h * 0.55;
      for (let k = 0; k < 5; k += 1) {
        const top = Math.random() < 0.5;
        q.pours.push({ x: top ? rand(0, S.W) : (Math.random() < 0.5 ? rand(-10, S.W * 0.1) : rand(S.W * 0.9, S.W + 10)), y: top ? rand(-10, S.H * 0.1) : rand(0, S.H), tx: cx, ty: cy, age: 0, life: rand(0.6, 1.1), rgb: Math.random() < 0.5 ? THEM : YOU, cx: 0, cy: 0 });
      }
    }
    for (let k = q.pours.length - 1; k >= 0; k -= 1) {
      const o = q.pours[k]; o.age += dt;
      if (o.age > o.life) { q.pours.splice(k, 1); continue; }
      const u = ease(o.age / o.life); o.cx = lerp(o.x, o.tx, u); o.cy = lerp(o.y, o.ty, u);
    }
    if (q.phase === 'zoom' && age() >= ZOOM_S) { q.phase = 'held'; q.t0 = S.t; }
    if (q.phase === 'held' && !q.entered) { q.entered = true; const cb = q.onEntered; q.onEntered = null; if (cb) setTimeout(cb, 0); }
    if (q.phase === 'sink' && age() > 0.8) { seq = null; return; }
    // the doors' light; while he really writes, he looks from door to door
    const writingLook = q.phase === 'wait' && S.writing && !S.still;
    if (writingLook && S.t - q.lookT > 1.6) { q.lookT = S.t; q.look = (q.look + 1) % q.doors.length; }
    if (q.phase === 'wait' && !S.writing) q.look = -1;
    const dimT = q.phase === 'sink' ? 1 : (q.phase === 'zoom' || q.phase === 'held') ? 0.2 : 0.45;
    q.dim += (dimT - q.dim) * (1 - Math.exp(-dt * 2.5));
    for (const d of q.doors) {
      const isPick = d === q.pick; const late = q.phase === 'pick' || q.phase === 'zoom' || q.phase === 'held';
      const aT = q.phase === 'sink' ? 0 : (late && !isPick) ? 0 : 1;
      d.a += (aT - d.a) * (1 - Math.exp(-dt * (aT ? 2.2 : 3)));
      const lookD = q.look >= 0 ? q.doors[q.look] : null;
      const litT = late ? (isPick ? 1.5 : 0) : lookD ? (lookD === d ? 1.1 : 0.3) : 0.45;
      d.lit += (litT - d.lit) * (1 - Math.exp(-dt * 3));
    }
    const target = q.look >= 0 ? q.doors[q.look].cx : (q.pick && (q.phase !== 'looks')) ? q.pick.cx : 0.5;
    q.rx += (target - q.rx) * (1 - Math.exp(-dt * 2.4)); q.ry += (0.88 - q.ry) * (1 - Math.exp(-dt * 2.4));
  }
  function drawChoice() {
    const q = seq; if (!q) return;
    const p = S.t - q.t0;
    // the room dims behind the doors (never to black)
    ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = (1 - q.dim) * 0.85;
    ctx.fillStyle = 'rgb(5,4,12)'; ctx.fillRect(0, 0, S.W, S.H);
    const rise = S.still || q.phase !== 'rise' ? 1 : easeOut(clamp(p / 1.4, 0, 1));
    const sink = q.phase === 'sink' ? easeOut(clamp(p / 0.8, 0, 1)) : 0;
    const z = q.phase === 'held' ? 1 : q.phase === 'zoom' ? ease(clamp(p / ZOOM_S, 0, 1)) : 0;
    const full = { x: 0, y: 0, w: S.W, h: S.H };
    for (const d of q.doors) {
      if (d.a < 0.01) continue;
      const lit = Math.min(1.5, d.lit); const zz = d === q.pick ? z : 0;
      const doorR = doorRect(d, ((1 - rise) + sink) * S.H * 0.3);
      let drawR = doorR; let clipR = doorR;
      let crop = imgReady(d.day) ? coverCrop(d.day, doorR, d.focus) : { x: 0, y: 0, w: 1, h: 1 };
      if (zz > 0 && imgReady(d.day)) {
        const big = { x: -0.35 * S.W, y: -0.9 * S.H, w: 1.7 * S.W, h: 2.2 * S.H };
        drawR = lerpRect(doorR, full, zz); clipR = lerpRect(doorR, big, zz);
        crop = lerpRect(crop, coverCrop(d.day, full, d.focus), zz);
      }
      ctx.save(); if (zz < 1) { archPath(clipR); ctx.clip(); }
      drawPainting(d, crop, drawR, d.a * lerp(0.28 + 0.45 * Math.min(1, lit), 1, zz));
      ctx.restore();
      if (zz < 1) {
        ctx.save(); ctx.globalCompositeOperation = 'lighter'; archPath(clipR);
        [[9, 0.07], [4, 0.16], [1.6, 0.85]].forEach(([lw, a]) => {
          ctx.lineWidth = lw * sc(); ctx.globalAlpha = 1;
          ctx.strokeStyle = `rgba(210,250,255,${a * Math.min(1, lit) * d.a * (1 - zz)})`; ctx.stroke();
        });
        ctx.restore();
        ctx.save(); ctx.globalCompositeOperation = 'lighter'; ctx.translate(doorR.x + doorR.w / 2, doorR.y + doorR.h); ctx.scale(1, 0.22);
        spr(SPR.themGlow, 0, 0, doorR.w * 2.6, 0.45 * Math.min(1, lit) * d.a * (1 - zz)); ctx.restore();
      }
    }
    ctx.globalCompositeOperation = 'lighter';
    for (const o of q.pours) { ctx.globalAlpha = (1 - o.age / o.life) * 0.9; ctx.fillStyle = `rgb(${o.rgb})`; ctx.fillRect(o.cx - 1.2, o.cy - 1.2, 2.4, 2.4); }
    const ra = q.phase === 'held' ? 0 : q.phase === 'zoom' ? 1 - clamp(p / 0.6, 0, 1) : 1;
    if (ra > 0.01) {
      const x = q.rx * S.W; const y = q.ry * S.H; const D = medD() * 0.85;
      spr(SPR.themGlow, x, y, D * 3.2, 0.5 * ra);
      drawPortrait(x, y, D, ra);
      if (S.writing && q.phase === 'wait') drawBubble(x + D * 0.6, y - D * 0.85, D);
    }
  }


  // ── the hatch (W3, §5a; the light-room mockup's scene 1) ──
  // A new port's first moment: dark → a seed of light → it cracks (one flash,
  // once: never a strobe) → sparks gather into his portrait → his REAL first
  // words rise as light → the portrait settles at his spot and the room wakes.
  // The seed WAITS for his words (the arrival message lands after the page
  // does); none in 40 s and he hatches silent. Nothing here invents a word.
  let targets = null;
  function buildTargets() {
    try {
      const N = 72; const c = mk(N, N); const g = c.getContext('2d'); if (!g || !portraitReady) return;
      const iw = portrait.naturalWidth; const ih = portrait.naturalHeight; const ins = iw * 0.08;
      g.drawImage(portrait, ins, ins, iw - 2 * ins, ih - 2 * ins, 0, 0, N, N);
      const d = g.getImageData(0, 0, N, N).data; // a tainted (no-CORS) portrait throws: the cyan fallback
      const out = [];
      for (let y = 0; y < N; y += 1) {
        for (let x = 0; x < N; x += 1) {
          const dx = ((x + 0.5) / N) * 2 - 1; const dy = ((y + 0.5) / N) * 2 - 1;
          if (dx * dx + dy * dy > 0.96) continue;
          const i = (y * N + x) * 4;
          out.push({ dx, dy, rgb: `${Math.min(255, d[i] * 1.15 + 18) | 0},${Math.min(255, d[i + 1] * 1.15 + 18) | 0},${Math.min(255, d[i + 2] * 1.15 + 24) | 0}` });
        }
      }
      targets = out;
    } catch (e) { targets = null; }
  }
  function fallbackTargets(n) {
    const out = [];
    for (let i = 0; i < n; i += 1) { const a = rand(0, TAU); const r = Math.sqrt(Math.random()) * 0.97; out.push({ dx: Math.cos(a) * r, dy: Math.sin(a) * r, rgb: THEM }); }
    return out;
  }
  function crackLines(cx, cy, s) {
    const lines = [];
    for (let i = 0; i < 6; i += 1) {
      let a = rand(0, TAU); let x = cx + rand(-0.2, 0.2) * s; let y = cy + rand(-0.3, 0.3) * s;
      const pts = [[x, y]];
      for (let k = 0; k < 6; k += 1) { a += rand(-0.7, 0.7); x += Math.cos(a) * s * 0.2; y += Math.sin(a) * s * 0.26; pts.push([x, y]); }
      lines.push(pts);
    }
    return lines;
  }
  const HATCH_WAIT_S = 40;
  let hatchSeq = null;
  function hatchStart({ line = '', onDone = null } = {}) {
    if (hatchSeq) return;
    hatchSeq = { t0: S.t, tc: null, fired: {}, parts: [], line: String(line || '').trim(), onDone, dim: 1, Dp: 0, seed: 0 };
    if (!S.running) draw();
  }
  function hatchLineIn(text) {
    const t = String(text || '').trim();
    if (hatchSeq && !hatchSeq.line && t) hatchSeq.line = t;
  }
  // p = the scene's clock on the mockup's timeline (crack at 2.3 s); before the
  // crack it holds at the seed, pulsing, until his words are in (or the wait ends)
  function hatchP(q) { return q.tc == null ? Math.min(2.2, S.t - q.t0) : 2.3 + (S.t - q.tc); }
  function updateHatch(dt) {
    const q = hatchSeq; if (!q) return;
    const cx = S.W / 2; const cy = S.H * 0.5; const s0 = Math.min(S.W, S.H) * 0.085;
    q.cx = cx; q.cy = cy; q.s0 = s0;
    const waited = S.t - q.t0;
    if (q.tc == null && waited >= 2.3 && (q.line || waited >= HATCH_WAIT_S || S.still)) q.tc = S.t;
    const p = hatchP(q);
    const once = (k) => (q.fired[k] ? false : (q.fired[k] = true));
    q.dim += ((p < 8 ? 0.12 : 1) - q.dim) * (1 - Math.exp(-dt * 2));
    q.seed = s0 * easeOut(clamp((p - 0.8) / 1.5, 0, 1));
    if (p >= 2.3 && once('crack')) { q.cracks = crackLines(cx, cy, s0); bell(392, 0.05, 1.6); }
    if (p >= 3.2 && once('burst')) {
      const N = S.still ? 0 : (S.phone ? 520 : 1100);
      for (let i = 0; i < N; i += 1) {
        const a = rand(0, TAU); const r = s0 * rand(0.2, 0.6); const v = rand(60, 260) * sc();
        q.parts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r * 1.2, vx: Math.cos(a) * v, vy: Math.sin(a) * v, mix: 0 });
      }
      gust();
    }
    if (p >= 4 && once('gather')) {
      q.Dp = Math.min(S.W, S.H) * (S.phone ? 0.62 : 0.58);
      const T = targets && targets.length ? targets : fallbackTargets(1200);
      for (const pt of q.parts) {
        const t = T[(Math.random() * T.length) | 0];
        Object.assign(pt, { fx: pt.x, fy: pt.y, tx: cx + (t.dx * q.Dp) / 2, ty: cy + (t.dy * q.Dp) / 2, trgb: t.rgb, dl: rand(0, 0.5), sw: rand(10, 40) * sc(), sa: rand(0, TAU) });
      }
      if (S.still) q.Dp = Math.min(S.W, S.H) * (S.phone ? 0.62 : 0.58);
    }
    if (p >= 7.5 && once('words')) {
      // his REAL first words, or none at all
      if (q.line) sayLight(q.line, { x: cx, y: Math.max(S.H * 0.1, cy - q.Dp / 2 - S.H * 0.06), life: 4.2, rise: false });
      bell(783.99, 0.1, 2.8); bell(1174.66, 0.05, 2.2, 0.12);
      themRipple();
    }
    for (const pt of q.parts) {
      if (!q.fired.gather) {
        pt.vx *= Math.exp(-dt * 2.4); pt.vy *= Math.exp(-dt * 2.4); pt.x += pt.vx * dt; pt.y += pt.vy * dt;
      } else {
        const u = clamp((p - 4 - pt.dl) / 2.1, 0, 1); const e = ease(u); const swirl = (1 - e) * pt.sw;
        pt.x = lerp(pt.fx, pt.tx, e) + Math.cos(pt.sa + u * 3) * swirl;
        pt.y = lerp(pt.fy, pt.ty, e) + Math.sin(pt.sa + u * 3) * swirl;
        pt.mix = e;
      }
    }
    if (p >= 10.4) {
      const cb = q.onDone; hatchSeq = null;
      if (cb) setTimeout(cb, 0);
    }
  }
  function drawHatch() {
    const q = hatchSeq; if (!q || !q.s0) return;
    const p = hatchP(q); const cx = q.cx; const cy = q.cy;
    // the room dims to near-dark around the seed (never black), then wakes
    ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = (1 - q.dim) * 0.88;
    ctx.fillStyle = 'rgb(5,4,12)'; ctx.fillRect(0, 0, S.W, S.H);
    ctx.globalCompositeOperation = 'lighter';
    if (p > 0.8 && p < 3.5) {
      const sd = q.seed; const fade = p < 3.2 ? 1 : 1 - (p - 3.2) / 0.3;
      const pulse = q.tc == null && !S.still ? 1 + 0.08 * Math.sin(S.t * 2.4) : 1;
      const wob = S.still ? 0 : Math.sin(p * 2.1) * 0.04 * (p > 2.3 ? 2.2 : 1);
      spr(SPR.white, cx, cy, sd * 7 * pulse, 0.55 * fade);
      spr(SPR.themGlow, cx, cy, sd * 10 * pulse, 0.35 * fade);
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(wob); ctx.globalAlpha = Math.max(0, fade);
      const eh = sd * 2.4 * pulse; if (SPR.themEgg) ctx.drawImage(SPR.themEgg, -eh * 0.385, -eh / 2, eh * 0.77, eh); ctx.restore();
      if (q.cracks) {
        const cp = clamp((p - 2.3) / 0.7, 0, 1);
        ctx.save(); ctx.globalAlpha = Math.max(0, fade); ctx.strokeStyle = 'rgba(255,255,255,0.95)';
        ctx.lineWidth = 1.6 * sc(); ctx.shadowColor = `rgba(${THEM},1)`; ctx.shadowBlur = 10;
        for (const L of q.cracks) {
          const n = Math.max(2, Math.round(L.length * cp));
          ctx.beginPath(); ctx.moveTo(L[0][0], L[0][1]);
          for (let i = 1; i < n; i += 1) ctx.lineTo(L[i][0], L[i][1]);
          ctx.stroke();
        }
        ctx.restore();
      }
    }
    // the one flash of the burst: a single event, never a strobe (≤ 3/s rule)
    if (!S.still && p >= 3.2 && p < 3.9) { const u = (p - 3.2) / 0.7; spr(SPR.white, cx, cy, q.s0 * (4 + 30 * u), 0.9 * (1 - u)); }
    if (q.parts.length) {
      const fadeOut = p < 6.6 ? 1 : clamp(1 - (p - 6.6) / 1.0, 0, 1);
      ctx.globalAlpha = 0.85 * fadeOut;
      for (const pt of q.parts) {
        ctx.fillStyle = pt.mix > 0.5 && pt.trgb ? `rgb(${pt.trgb})` : `rgb(${WHITE})`;
        ctx.fillRect(pt.x - 1.05, pt.y - 1.05, 2.1, 2.1);
      }
    }
    if (p >= 6.4 && q.Dp) {
      const a = clamp((p - 6.4) / 1.1, 0, 1); const home = resXY();
      if (S.still) {
        // reduced motion: nothing travels, the big portrait fades out as his own fades in at his spot
        const u = clamp((p - 8.6) / 1.6, 0, 1);
        ctx.globalCompositeOperation = 'lighter';
        drawPortrait(cx, cy, q.Dp, a * (1 - u));
        spr(SPR.themGlow, home.x, home.y, medD() * 3.6, 0.5 * u); drawPortrait(home.x, home.y, medD(), u);
      } else {
        const g = ease(clamp((p - 8.6) / 1.6, 0, 1));
        const D = lerp(q.Dp, medD(), g); const x = lerp(cx, home.x, g); const y = lerp(cy, home.y, g);
        ctx.globalCompositeOperation = 'lighter'; spr(SPR.themGlow, x, y, D * 3, 0.5 * a);
        drawPortrait(x, y, D, a);
      }
    }
  }

  // ── loop ──
  function update(dt) {
    S.w = clockWeights(S.minutes / 60);
    refreshAmbient();
    updateLamps(dt); updateFlowers(dt); updateWords(dt); updateChoice(dt); updateHatch(dt); updateLights(dt);
    S.nightLight += (S.nightTarget - S.nightLight) * (1 - Math.exp(-dt * (S.still ? 60 : 0.8)));
  }
  let draws = 0;
  let sceneMark = '';
  function markScene() {
    // the scene's phase for the browser gate: written on CHANGE only
    const m = hatchSeq ? `hatch-${hatchSeq.tc == null ? 'seed' : 'open'}` : seq ? seq.phase : '';
    if (m !== sceneMark) { sceneMark = m; if (m) canvas.setAttribute('data-lr-scene', m); else canvas.removeAttribute('data-lr-scene'); }
  }
  function draw() {
    if (!S.W || !S.H) return;
    markScene();
    // a cheap, throttled receipt the browser gate reads (verify-ported-place)
    draws += 1;
    if (draws <= 3 || draws % 600 === 0) canvas.setAttribute('data-lr-draws', String(draws));
    ctx.setTransform(S.dpr, 0, 0, S.dpr, 0, 0);
    drawGround(); drawStars(); drawLamps(); drawFlowers();
    // 💡 his lights draw right after him, and only when the hatch and the
    // choice scenes are not up (those own the stage)
    if (hatchSeq) { drawHatch(); drawWords(); } else if (seq) { drawChoice(); if (seq.phase !== 'held') drawWords(); } else { drawWords(); drawResident(); drawLights(); }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  function frame(ts) {
    S.raf = requestAnimationFrame(frame);
    const now = ts / 1000; const dt = S.last ? Math.min(0.05, now - S.last) : 0.016; S.last = now;
    S.t += dt; update(dt); draw();
  }
  function start() { if (S.running || S.destroyed) return; S.running = true; S.last = 0; S.raf = requestAnimationFrame(frame); }
  function stop() { S.running = false; cancelAnimationFrame(S.raf); }
  function syncLoop() { if (!document.hidden && S.onScreen && !S.destroyed) start(); else stop(); }
  const onVis = () => syncLoop();
  document.addEventListener('visibilitychange', onVis);
  let io = null;
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver((es) => { S.onScreen = es[es.length - 1].isIntersecting; syncLoop(); }, { threshold: 0.02 });
    io.observe(canvas);
  }

  // ── pointer: tap a lamp to light it, tap the dark to grow flowers ──
  const local = (e) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const onMove = (e) => { const p = local(e); S.px = p.x; S.py = p.y; S.pointerIn = true; };
  const onLeave = () => { S.pointerIn = false; };
  const onDown = (e) => {
    if (S.sound) audio();
    const p = local(e); const l = nearestLamp(p.x, p.y, 30 * sc() * (S.phone ? 1.4 : 1));
    if (l) rippleFromLamp(l, 'you'); else bloom(p.x, p.y, S.phone ? 4 : 6);
  };
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerleave', onLeave);
  canvas.addEventListener('pointerdown', onDown);

  function resize() {
    const r = canvas.getBoundingClientRect(); if (!r.width || !r.height) return;
    const phone = r.width < 560; const first = S.phone === null; const changed = S.phone !== phone;
    S.phone = phone; S.dpr = Math.min(window.devicePixelRatio || 1, phone ? 1.5 : 2);
    S.W = r.width; S.H = r.height; canvas.width = Math.round(r.width * S.dpr); canvas.height = Math.round(r.height * S.dpr);
    if (first || changed) {
      buildWorld();
      if (first) for (let i = 0; i < 3; i += 1) spawnFlower(rand(0.12, 0.88) * S.W, rand(0.58, 0.9) * S.H, pick(flowerColors()), 0, { open: 1, age: rand(1, 5), ambient: true });
    }
    refreshAmbient();
    // resizing clears the bitmap: repaint now, never wait for a frame that a
    // stopped loop (scrolled away) would not deliver (verify-ported-place caught
    // a blank band after a layout change raced the visibility stop)
    draw();
  }

  return {
    resize,
    setClock(minutesOfDay, dayKey) {
      S.minutes = minutesOfDay; S.dayKey = dayKey || null; S.shower = dayKey ? showerOn(dayKey) : null;
      S.w = clockWeights(S.minutes / 60); refreshAmbient(); if (!S.running) draw();
    },
    setResident({ imageUrl } = {}) {
      if (imageUrl && portrait.src !== imageUrl) { portraitReady = false; portrait.src = imageUrl; }
    },
    setWriting(on) { S.writing = !!on; if (!S.running) draw(); },
    youSent() { youRipple(); if (S.nightTarget > 0) S.nightTarget = 0; },
    theySaid(text) {
      const line = String(text || '').trim();
      // during the hatch his first real words are the scene's to show
      if (hatchSeq) { hatchLineIn(line); return; }
      if (line && !seq) sayLight(line);
      themRipple();
      bell(pick([659.25, 783.99, 880]), 0.08, 2.6); bell(1318.51, 0.03, 1.8, 0.09);
    },
    setSound(on, { confirm = false } = {}) { S.sound = !!on; if (S.sound && confirm) { audio(); bell(880, 0.08, 2.2); } },
    setStill(on) { S.still = !!on; if (!S.running) draw(); },
    choiceStart,
    choiceReveal,
    choiceCancel,
    hatch: hatchStart,
    hatchLine: hatchLineIn,
    // 🌙 goodbye (W3): the owner waved; a soft falling pair of bells, the lamps
    // ease down to a night-light (one stays warm by him). off = back up.
    lampDown(on) {
      const next = on ? 1 : 0;
      if (next === S.nightTarget) return;
      S.nightTarget = next;
      if (on) { bell(659.25, 0.06, 2.4); bell(493.88, 0.05, 2.8, 0.35); }
      if (!S.running) draw();
    },
    // 💡 E2: the room's pictures today → how many of his four lights are lit
    setLights,
    // Coming back warms the room: flowers rest (never wilt) while nobody is
    // here and open again on return, with a gold ripple from your side.
    welcomeBack() {
      S.resting = true; flowers.forEach((f) => { f.open = 0.2; });
      setTimeout(() => { S.resting = false; youRipple(); }, 700);
    },
    destroy() {
      S.destroyed = true; stop();
      document.removeEventListener('visibilitychange', onVis);
      if (io) io.disconnect();
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('pointerdown', onDown);
      try { if (AC) AC.close(); } catch (e) { /* already closed */ }
    },
    // test seam: the loop's state, read-only
    _state: () => ({ running: S.running, lamps: lamps.length, words: words.length, writing: S.writing, sound: S.sound, scene: seq ? seq.phase : null, hatch: hatchSeq ? (hatchSeq.tc == null ? 'seed' : 'open') : null, nightLight: S.nightTarget, lights: { left: LIGHTS.left, of: LIGHTS.of, lit: LIGHTS.k.map((k) => Math.round(k * 100) / 100) } }),
    // test seam: one frame of the loop's easing without rAF (jsdom), dt in seconds
    _tick: (dt) => { S.t += dt; update(dt); },
    start: syncLoop,
  };
}
